import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { fullName } from '../../../shared/helpers/user.helpers';
import { UsersService } from '../../../shared/services/users.service';
import { ZonesService } from '../../../shared/services/zones.service';
import { FormMode } from '../../../shared/types/forms.types';
import {
  AdminCreateOperatorRequest,
  AdminUpdateOperatorRequest,
  LICENSE_TIER_OPTIONS,
  LicenseTier,
  Operator,
} from '../../../shared/types/operator.types';
import { User } from '../../../shared/types/user.types';

const TIER_OPTIONS: PrettySelectOption<LicenseTier | null>[] = [
  { value: null, label: 'Aucun palier' },
  ...LICENSE_TIER_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

/** Étape de résolution de la personne (mode création uniquement). */
type PersonMode = 'search' | 'found' | 'new';

/**
 * Formulaire opérateur (create/edit). En **création**, applique le flow
 * « rechercher d'abord » (§13.A) : l'admin tape un identifiant naturel
 * (email / téléphone / username), le back résout via `/auth/api/users/resolve`,
 * puis on enchaîne soit sur une personne trouvée (`userId`), soit sur un
 * formulaire nouveau compte (`firstName`/`lastName` + email/téléphone). Plus
 * aucun `id` interne saisi à la main.
 */
@Component({
  selector: 'app-operator-form',
  imports: [CommonModule, ReactiveFormsModule, PrettySelectComponent],
  templateUrl: './operator-form.component.html',
})
export class OperatorFormComponent implements OnChanges {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly users = inject(UsersService);
  private readonly zones = inject(ZonesService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.loadZones();
  }

  @Input() mode: FormMode = 'create';
  @Input() initial: Operator | null = null;
  @Input() submitting = false;
  @Input() serverError: string | null = null;

  @Output() readonly saveCreate = new EventEmitter<AdminCreateOperatorRequest>();
  @Output() readonly saveEdit = new EventEmitter<AdminUpdateOperatorRequest>();
  @Output() readonly cancel = new EventEmitter<void>();

  readonly tierOptions = TIER_OPTIONS;
  readonly fullName = fullName;

  // ── Zones sélectionnables ─────────────────────────────────────────────
  // La zone globale (code `*`) est exclue : elle porte les configs par défaut
  // (commissions), ce n'est pas une zone d'opération assignable.
  readonly zoneOptions = signal<PrettySelectOption<string>[]>([]);
  readonly zonesLoading = signal(false);
  readonly zonesError = signal<string | null>(null);

  // ── Résolution de la personne (create) ────────────────────────────────
  readonly personMode = signal<PersonMode>('search');
  readonly resolving = signal(false);
  readonly resolveError = signal<string | null>(null);
  readonly resolvedUser = signal<User | null>(null);

  readonly searchForm = this.fb.group({
    identifier: ['', [Validators.required]],
  });

  readonly form = this.fb.group({
    // Personne (nouveau compte) — requis seulement en personMode='new'.
    firstName: [''],
    lastName: [''],
    // Opérateur
    businessName: ['', [Validators.required, Validators.maxLength(200)]],
    zoneCode: ['', [Validators.required, Validators.maxLength(50)]],
    licenseTier: [null as LicenseTier | null],
    phone: [''],
    email: ['', [Validators.email, Validators.maxLength(200)]],
  });

  /** Identité de la personne déjà liée (mode edit) ou résolue (mode found). */
  readonly personLabel = computed(() => {
    const u = this.resolvedUser();
    if (u) return fullName(u);
    return '';
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initial'] || changes['mode']) {
      this.applyInitial();
    }
  }

  loadZones(): void {
    this.zonesLoading.set(true);
    this.zonesError.set(null);
    this.zones
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) => {
          this.zoneOptions.set(
            list
              .filter((z) => z.code !== '*')
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((z) => ({ value: z.code, label: z.name, hint: z.code })),
          );
          this.zonesLoading.set(false);
        },
        error: (err: unknown) => {
          this.zonesLoading.set(false);
          this.zonesError.set(toErrorMessage(err, 'Impossible de charger les zones.'));
        },
      });
  }

  private applyInitial(): void {
    const o = this.initial;
    this.form.reset({
      firstName: '',
      lastName: '',
      businessName: o?.businessName ?? '',
      zoneCode: o?.zoneCode ?? '',
      licenseTier: o?.licenseTier ?? null,
      phone: o?.phone ?? '',
      email: o?.email ?? '',
    });
    this.searchForm.reset({ identifier: '' });
    this.resolveError.set(null);
    this.resolvedUser.set(null);
    // En edit, la personne est figée → on saute l'étape de résolution.
    this.personMode.set(this.mode === 'edit' ? 'found' : 'search');
  }

  // ── Recherche / résolution ────────────────────────────────────────────
  search(): void {
    if (this.searchForm.invalid || this.resolving()) {
      this.searchForm.markAllAsTouched();
      return;
    }
    const identifier = this.searchForm.getRawValue().identifier.trim();
    this.resolving.set(true);
    this.resolveError.set(null);
    this.users
      .resolve(identifier)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (u) => {
          this.resolving.set(false);
          this.resolvedUser.set(u);
          // Pré-remplit le contact opérateur avec celui du user trouvé.
          this.form.patchValue({ email: u.email ?? '', phone: u.phone ?? '' });
          this.personMode.set('found');
        },
        error: (err: unknown) => {
          this.resolving.set(false);
          if (err instanceof HttpErrorResponse && err.status === 404) {
            // Aucun compte → bascule sur le formulaire nouveau compte.
            this.prefillNewFromIdentifier(identifier);
            this.personMode.set('new');
            return;
          }
          this.resolveError.set(toErrorMessage(err, 'errors.searchFailed'));
        },
      });
  }

  /** Devine email/téléphone à partir de l'identifiant tapé. */
  private prefillNewFromIdentifier(identifier: string): void {
    const isEmail = identifier.includes('@');
    const isPhone = /^[+0-9][0-9 ]{6,}$/.test(identifier);
    this.form.patchValue({
      firstName: '',
      lastName: '',
      email: isEmail ? identifier : '',
      phone: !isEmail && isPhone ? identifier : '',
    });
  }

  /** Repart sur la recherche (efface la personne résolue). */
  resetPerson(): void {
    this.resolvedUser.set(null);
    this.resolveError.set(null);
    this.searchForm.reset({ identifier: '' });
    this.form.patchValue({ firstName: '', lastName: '', email: '', phone: '' });
    this.personMode.set('search');
  }

  /** Au moins un canal de contact requis pour un nouveau compte. */
  private newAccountContactOk(): boolean {
    const raw = this.form.getRawValue();
    return !!(raw.email.trim() || raw.phone.trim());
  }

  newAccountInvalid(): boolean {
    const raw = this.form.getRawValue();
    return (
      !raw.firstName.trim() ||
      !raw.lastName.trim() ||
      !this.newAccountContactOk()
    );
  }

  onSubmit(): void {
    if (this.submitting) return;

    if (this.mode === 'edit') {
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
      const raw = this.form.getRawValue();
      this.saveEdit.emit({
        businessName: raw.businessName.trim(),
        zoneCode: raw.zoneCode.trim(),
        licenseTier: raw.licenseTier,
        phone: raw.phone.trim() || null,
        email: raw.email.trim() || null,
      });
      return;
    }

    // ── Création ──
    if (this.personMode() === 'search') {
      this.resolveError.set('Recherchez d’abord la personne (email, téléphone ou identifiant).');
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();

    if (this.personMode() === 'found') {
      const u = this.resolvedUser();
      if (!u) {
        this.resetPerson();
        return;
      }
      this.saveCreate.emit({
        userId: u.id,
        businessName: raw.businessName.trim(),
        zoneCode: raw.zoneCode.trim(),
        licenseTier: raw.licenseTier,
        phone: raw.phone.trim() || null,
        email: raw.email.trim() || null,
      });
    } else {
      // new
      if (this.newAccountInvalid()) {
        this.form.markAllAsTouched();
        return;
      }
      this.saveCreate.emit({
        firstName: raw.firstName.trim(),
        lastName: raw.lastName.trim(),
        businessName: raw.businessName.trim(),
        zoneCode: raw.zoneCode.trim(),
        licenseTier: raw.licenseTier,
        phone: raw.phone.trim() || null,
        email: raw.email.trim() || null,
      });
    }
  }

  onCancel(): void { this.cancel.emit(); }

  showError(name: keyof typeof this.form.controls): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.touched || c.dirty);
  }
}
